import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, FileText, FileWarning, FilePlus2, Banknote, ExternalLink, Calendar, User, UserCheck, AlertCircle, Download, Send, CheckCircle2, Loader2, Mail, BellOff, Trash2, PencilLine, Ban } from "lucide-react";
import { propsFundoModal } from '../../src/lib/backdropModal';

export type DocTipo = 'distrato' | 'advertencia' | 'mora' | 'aditivo';

interface TermHistoryItem {
    url_pdf: string;
    data_criacao: string;
    tipo_cliente?: string;
    numero_termo?: string;
    /** Usuário do VigorHub que gerou o documento */
    enviado_por?: string | null;
    /** Associado que recebeu o documento */
    associado?: string | null;
    /** Prazo de regularização: 5º dia útil (mora) / 15º dia corrido (advertência) após o envio ao associado */
    prazo_final?: string | null;
    resultado_verificacao?: string | null;
    enviado_cliente_em?: string | null;
    /** Novo fluxo (jul/2026): quando o representante legal assinou no Autentique */
    assinado_em?: string | null;
    /** Id do documento no Autentique — usado para montar o link do PDF assinado */
    id_autentique?: string | null;
    /** Id da linha no log — usado para gravar o registro de envio */
    log_id?: number | null;
    /** 'advertencia' | 'mora' — só estes têm o fluxo de envio manual */
    doc_tipo?: DocTipo;
    /** Nome de quem registrou o envio manual ao cliente */
    enviado_cliente_por_nome?: string | null;
    /** Último lembrete de assinatura enviado ao procurador (e-mail + in-app) */
    lembrete_enviado_em?: string | null;
    /** Nome de quem clicou em "Enviar lembrete" */
    lembrete_enviado_por_nome?: string | null;
    /** Quando alguém decidiu, de propósito, NÃO encaminhar o documento ao associado (alternativa a enviado_cliente_em) */
    nao_enviado_cliente_em?: string | null;
    /** Observação explicando por que o documento não foi encaminhado */
    nao_enviado_cliente_motivo?: string | null;
    /** Nome de quem registrou a decisão de não enviar */
    nao_enviado_cliente_por_nome?: string | null;
    /** Quando o documento foi cancelado. Preenchido => a linha vira histórico morto. */
    cancelado_em?: string | null;
    /** Por que foi cancelado — obrigatório na hora de cancelar. */
    cancelado_motivo?: string | null;
    /** 'excluir' (apagado do Autentique) | 'anular' (já assinado, marcado como recusado) */
    cancelado_acao?: 'excluir' | 'anular' | null;
    /** O que de fato aconteceu no Autentique: removido | invalidado | falhou | sem_documento */
    autentique_resultado?: 'removido' | 'invalidado' | 'falhou' | 'sem_documento' | null;
    /** Nome de quem cancelou */
    cancelado_por_nome?: string | null;
    /** Payload exato mandado ao n8n na geração — alimenta o "Corrigir e gerar novamente" */
    payload_geracao?: Record<string, any> | null;
    /** Só no aditivo: qual dos três foi gerado. NULL nos registros anteriores a set/2026. */
    tipo_aditivo?: 'transferencia' | 'adicao' | 'desconto' | null;
}

interface TermCancelHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    terms: TermHistoryItem[] | null | undefined;
    advertencias?: TermHistoryItem[] | null;
    moras?: TermHistoryItem[] | null;
    aditivos?: TermHistoryItem[] | null;
    /** Grava a data em que o emissor enviou manualmente o PDF assinado ao cliente. */
    onRegistrarEnvio?: (logId: number, docTipo: 'advertencia' | 'mora', dataISO: string) => Promise<void>;
    /** Envia lembrete de assinatura pendente ao procurador (e-mail + notificação in-app). */
    onEnviarLembrete?: (logId: number, docTipo: 'advertencia' | 'mora') => Promise<void>;
    /** Registra que o documento não será encaminhado ao associado, com o motivo. */
    onRegistrarNaoEnvio?: (logId: number, docTipo: 'advertencia' | 'mora', motivo: string) => Promise<void>;
    /**
     * Cancela o documento: apaga no Autentique se ninguém assinou, ou marca como
     * recusado se já foi assinado. Devolve `true` quando deu certo — o modal só
     * fecha o formulário de motivo nesse caso.
     */
    onCancelarDocumento?: (logId: number, docTipo: DocCancelavel, motivo: string) => Promise<boolean>;
    /** Cancela e reabre o modal de geração já preenchido, para refazer o documento. */
    onCorrigirDocumento?: (term: TermHistoryItem, motivo: string) => Promise<boolean>;
    /** false quando o papel do usuário não tem acesso a termo_cancelamento_logs (RLS) — muda a mensagem
     *  vazia da aba "Distratos" pra não parecer que a UC nunca teve um. */
    podeVerDistratos?: boolean;
}

type AbaId = 'distratos' | 'advertencias' | 'moras' | 'aditivos';

/** Aditivo fica de fora: ele altera titularidade/situação das UCs na geração, então
 *  desfazer exigiria reverter esses UPDATEs — outra conversa. */
export type DocCancelavel = 'distrato' | 'advertencia' | 'mora';

/** Rótulo curto do subtipo do aditivo, exibido ao lado do PF/PJ. */
const ADITIVO_LABEL: Record<'transferencia' | 'adicao' | 'desconto', string> = {
    transferencia: 'Transferência',
    adicao: 'Adição de UC',
    desconto: 'Desconto',
};

const ABA_DOC_TIPO: Record<AbaId, DocTipo> = {
    distratos: 'distrato',
    advertencias: 'advertencia',
    moras: 'mora',
    aditivos: 'aditivo',
};

const sortByDate = (items: TermHistoryItem[] | null | undefined): TermHistoryItem[] =>
    items && Array.isArray(items)
        ? [...items].sort((a, b) => {
              const timeA = new Date(a.data_criacao).getTime();
              const timeB = new Date(b.data_criacao).getTime();
              return (isNaN(timeB) ? 0 : timeB) - (isNaN(timeA) ? 0 : timeA);
          })
        : [];

/** Data de hoje em yyyy-mm-dd (fuso local) para o valor padrão do date picker. */
const hojeISO = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const linkPdfAssinado = (term: TermHistoryItem): string =>
    term.id_autentique
        ? `https://api.autentique.com.br/documentos/${term.id_autentique}/assinado.pdf`
        : term.url_pdf;

const TermCancelHistoryModal: React.FC<TermCancelHistoryModalProps> = ({
    isOpen,
    onClose,
    terms,
    advertencias,
    moras,
    aditivos,
    onRegistrarEnvio,
    onEnviarLembrete,
    onRegistrarNaoEnvio,
    onCancelarDocumento,
    onCorrigirDocumento,
    podeVerDistratos = true,
}) => {
    const [aba, setAba] = useState<AbaId>('distratos');
    // Linha em que o usuário está registrando o envio manual (por log_id + tipo)
    const [registrandoKey, setRegistrandoKey] = useState<string | null>(null);
    const [dataEnvio, setDataEnvio] = useState<string>(hojeISO());
    const [salvando, setSalvando] = useState(false);
    // Linha em que o lembrete de assinatura está sendo enviado (por log_id + tipo)
    const [enviandoLembreteKey, setEnviandoLembreteKey] = useState<string | null>(null);
    // Linha em que o usuário está registrando "não enviar" + motivo (por log_id + tipo)
    const [naoEnviandoKey, setNaoEnviandoKey] = useState<string | null>(null);
    const [motivoNaoEnvio, setMotivoNaoEnvio] = useState<string>('');
    const [salvandoNaoEnvio, setSalvandoNaoEnvio] = useState(false);
    // Linha em que o usuário está cancelando (ou corrigindo) o documento, e qual das duas
    // saídas ele escolheu — as duas passam pelo mesmo formulário de motivo.
    const [cancelandoKey, setCancelandoKey] = useState<string | null>(null);
    const [cancelandoIntencao, setCancelandoIntencao] = useState<'cancelar' | 'corrigir'>('cancelar');
    const [motivoCancelamento, setMotivoCancelamento] = useState<string>('');
    const [salvandoCancelamento, setSalvandoCancelamento] = useState(false);

    const fecharFormCancelamento = () => {
        setCancelandoKey(null);
        setMotivoCancelamento('');
    };

    useEffect(() => {
        if (isOpen) {
            setAba('distratos');
            setRegistrandoKey(null);
            setNaoEnviandoKey(null);
            fecharFormCancelamento();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const abas: { id: AbaId; label: string; icon: React.ReactNode; items: TermHistoryItem[]; activeCls: string; vazio: string }[] = [
        {
            id: 'distratos', label: 'Distratos', icon: <FileText size={15} />, items: sortByDate(terms),
            activeCls: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400',
            vazio: podeVerDistratos
                ? 'Esta unidade ainda não possui termos de cancelamento gerados.'
                : 'Seu perfil não tem acesso ao histórico de distratos desta unidade — fale com um administrador ou gerente.'
        },
        {
            id: 'advertencias', label: 'Advertências', icon: <FileWarning size={15} />, items: sortByDate(advertencias),
            activeCls: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 text-amber-600 dark:text-amber-400',
            vazio: 'Esta unidade ainda não possui comunicados de advertência gerados.'
        },
        {
            id: 'moras', label: 'Notificação Extrajudicial', icon: <Banknote size={15} />, items: sortByDate(moras),
            activeCls: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/50 text-rose-600 dark:text-rose-400',
            vazio: 'Esta unidade ainda não possui notificações extrajudiciais geradas.'
        },
        {
            id: 'aditivos', label: 'Aditivos', icon: <FilePlus2 size={15} />, items: sortByDate(aditivos),
            activeCls: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50 text-blue-600 dark:text-blue-400',
            vazio: 'Esta unidade ainda não possui aditivos contratuais gerados.'
        },
    ];
    const abaAtiva = abas.find(a => a.id === aba) || abas[0];
    const sortedTerms = abaAtiva.items;

    // Advertência e Notificação Extrajudicial seguem o fluxo de assinatura + envio manual + prazo.
    const temFluxoEnvio = aba === 'moras' || aba === 'advertencias';
    const docTipo: 'advertencia' | 'mora' = aba === 'advertencias' ? 'advertencia' : 'mora';
    const prazoLabel = aba === 'moras' ? '5 dias úteis' : '15 dias';
    // Distrato, advertência e notificação podem ser desfeitos. Aditivo não: ele já
    // mexeu na titularidade e na situação das UCs quando foi gerado.
    const podeCancelar = aba !== 'aditivos' && !!(onCancelarDocumento || onCorrigirDocumento);
    const docTipoCancelavel = ABA_DOC_TIPO[aba] as DocCancelavel;
    // A coluna "Situação" existe onde há algo a mostrar: fluxo de envio ou cancelamento.
    const temColunaSituacao = temFluxoEnvio || aba === 'distratos';

    // Situação do documento (só advertência e notificação extrajudicial têm ciclo de assinatura/envio/prazo)
    type StatusDoc = { label: string; cls: string; title: string; key: 'cancelado' | 'nao_enviado' | 'resultado' | 'prazo' | 'assinado_enviar' | 'expirado' | 'aguardando' | 'ativo' };
    const statusDoc = (term: TermHistoryItem): StatusDoc => {
        // -1) Cancelado ganha de tudo: a linha é histórico, não tem mais ciclo de vida.
        if (term.cancelado_em) {
            const anulado = term.cancelado_acao === 'anular';
            const naoConfirmado = term.autentique_resultado === 'falhou';
            return {
                label: anulado ? 'Anulado' : 'Excluído',
                cls: naoConfirmado
                    ? 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50'
                    : 'text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700',
                title: [
                    anulado
                        ? 'Documento já assinado — foi marcado como recusado no Autentique, o PDF continua acessível.'
                        : 'Documento apagado do Autentique. O link de assinatura não existe mais.',
                    term.cancelado_motivo ? `Motivo: ${term.cancelado_motivo}` : null,
                    naoConfirmado ? 'Atenção: o Autentique não confirmou a operação — confira pelo painel.' : null,
                ].filter(Boolean).join(' '),
                key: 'cancelado',
            };
        }
        // Fora do fluxo de envio (distrato), "não cancelado" é tudo que há para dizer.
        if (!temFluxoEnvio) {
            return {
                label: 'Ativo',
                cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/50',
                title: 'Documento válido, disponível para assinatura no Autentique.',
                key: 'ativo',
            };
        }
        // 0) Decisão deliberada de não encaminhar ao associado — sempre tem prioridade.
        if (term.nao_enviado_cliente_em) {
            return {
                label: 'Não enviado',
                cls: 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700',
                title: term.nao_enviado_cliente_motivo
                    ? `Não será encaminhado ao associado. Motivo: ${term.nao_enviado_cliente_motivo}`
                    : 'Não será encaminhado ao associado.',
                key: 'nao_enviado'
            };
        }
        const RESULTADOS: Record<string, StatusDoc> = {
            pago:          { label: 'Pago',      cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/50', title: 'Débito quitado dentro do prazo', key: 'resultado' },
            parcelado:     { label: 'Parcelado', cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/50', title: 'Débito parcelado — não negativar', key: 'resultado' },
            parcial:       { label: 'Parcial',   cls: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50', title: 'Pago em parte — confira antes de negativar', key: 'resultado' },
            nao_pago:      { label: 'Não pago',  cls: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800/50', title: 'Prazo vencido sem pagamento — apto a negativação', key: 'resultado' },
            indeterminado: { label: 'Verificar', cls: 'text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700', title: 'Sem faturas vinculadas — confira manualmente', key: 'resultado' },
            prazo_encerrado: { label: 'Prazo encerrado', cls: 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700', title: 'Prazo de 15 dias encerrado — verifique se o associado regularizou a exclusividade', key: 'resultado' },
        };

        // 1) Já enviado ao cliente: mostra resultado da verificação ou o prazo em contagem
        if (term.enviado_cliente_em) {
            if (term.resultado_verificacao && RESULTADOS[term.resultado_verificacao]) {
                return RESULTADOS[term.resultado_verificacao];
            }
            if (term.prazo_final) {
                const [ano, mes, dia] = String(term.prazo_final).split('-');
                return {
                    label: `Até ${dia}/${mes}`,
                    cls: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/50',
                    title: `Prazo de ${prazoLabel} encerra em ${dia}/${mes}/${ano}`,
                    key: 'prazo'
                };
            }
        }

        // 2) Assinado pelo representante, mas o emissor ainda não registrou o envio
        if (term.assinado_em) {
            return {
                label: 'Assinado — enviar',
                cls: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-800/50',
                title: 'Assinado pelo representante legal. Baixe o PDF assinado, envie ao cliente e registre a data de envio para iniciar o prazo.',
                key: 'assinado_enviar'
            };
        }

        // 3) Ainda sem assinatura. Distingue "esperando" de "documento morto" (Autentique invalida após 7 dias).
        const diasParado = Math.floor((Date.now() - new Date(term.data_criacao).getTime()) / 86400000);
        if (Number.isFinite(diasParado) && diasParado > 7) {
            return {
                label: 'Expirado',
                cls: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50',
                title: `Gerado há ${diasParado} dias e nunca assinado — o documento perdeu a validade no Autentique e precisa ser gerado de novo`,
                key: 'expirado'
            };
        }
        return {
            label: 'Aguardando assinatura',
            cls: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50',
            title: 'Aguardando a assinatura do representante legal. Enquanto não assinar, você não consegue enviar o documento ao associado.',
            key: 'aguardando'
        };
    };

    const formatSafeDate = (dateString: string | undefined | null) => {
        if (!dateString) return "-";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    const formatDia = (dateString: string | undefined | null) => {
        if (!dateString) return "-";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const confirmarEnvio = async (term: TermHistoryItem) => {
        if (!onRegistrarEnvio || !term.log_id) return;
        // Meio-dia em -03:00 (Brasil sem horário de verão) evita o documento "voltar um dia" por fuso.
        const dataISO = new Date(`${dataEnvio}T12:00:00-03:00`).toISOString();
        try {
            setSalvando(true);
            await onRegistrarEnvio(term.log_id, docTipo, dataISO);
            setRegistrandoKey(null);
        } finally {
            setSalvando(false);
        }
    };

    const enviarLembrete = async (term: TermHistoryItem, rowKey: string) => {
        if (!onEnviarLembrete || !term.log_id) return;
        try {
            setEnviandoLembreteKey(rowKey);
            await onEnviarLembrete(term.log_id, docTipo);
        } finally {
            setEnviandoLembreteKey(null);
        }
    };

    const confirmarNaoEnvio = async (term: TermHistoryItem) => {
        if (!onRegistrarNaoEnvio || !term.log_id || !motivoNaoEnvio.trim()) return;
        try {
            setSalvandoNaoEnvio(true);
            await onRegistrarNaoEnvio(term.log_id, docTipo, motivoNaoEnvio.trim());
            setNaoEnviandoKey(null);
            setMotivoNaoEnvio('');
        } finally {
            setSalvandoNaoEnvio(false);
        }
    };

    const MOTIVO_MINIMO = 5;

    const confirmarCancelamento = async (term: TermHistoryItem) => {
        const motivo = motivoCancelamento.trim();
        if (!term.log_id || motivo.length < MOTIVO_MINIMO) return;
        try {
            setSalvandoCancelamento(true);
            const ok = cancelandoIntencao === 'corrigir'
                ? await onCorrigirDocumento?.(term, motivo)
                : await onCancelarDocumento?.(term.log_id, docTipoCancelavel, motivo);
            // Só limpa o formulário quando deu certo: se o Autentique recusou, o usuário
            // mantém o que escreveu e vê o toast de erro.
            if (ok) fecharFormCancelamento();
        } finally {
            setSalvandoCancelamento(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 rounded-lg border border-red-100 dark:border-red-800/50">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Histórico de Documentos</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Quem enviou, quem recebeu e a situação de cada documento gerado</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {/* Abas */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {abas.map(a => (
                            <button
                                key={a.id}
                                onClick={() => { setAba(a.id); setRegistrandoKey(null); setNaoEnviandoKey(null); fecharFormCancelamento(); }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                                    aba === a.id
                                        ? a.activeCls
                                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                                }`}
                            >
                                {a.icon} {a.label} ({a.items.length})
                            </button>
                        ))}
                    </div>

                    {sortedTerms.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-100 dark:border-slate-700/50 rounded-xl bg-gray-50/30 dark:bg-slate-800/30">
                            <div className="w-12 h-12 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-gray-400 dark:text-slate-500 mb-3">
                                <AlertCircle size={24} />
                            </div>
                            <h3 className="text-gray-900 dark:text-slate-100 font-medium">Nenhum documento encontrado</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-xs mt-1">{abaAtiva.vazio}</p>
                        </div>
                    ) : (
                        <>
                        {temFluxoEnvio && (
                            <div className="mb-3 text-xs text-gray-500 dark:text-slate-400 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-800/40 rounded-lg px-3 py-2">
                                Quando o representante legal assina, o sistema avisa quem emitiu. <strong>Baixe o PDF assinado, envie ao cliente</strong> e depois <strong>registre a data de envio</strong> —
                                o prazo de <strong>{prazoLabel}</strong> só começa a partir dela.
                            </div>
                        )}
                        <div className="overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-xl shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                                <thead className="bg-gray-50 dark:bg-slate-800/50">
                                    {/* 4 colunas, não 7: com sete a tabela estourava a largura do modal e a
                                        coluna "Ação" só aparecia rolando na horizontal. Número+tipo e
                                        associado+autor+data viraram uma célula cada, empilhados. */}
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Documento</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Associado</th>
                                        {temColunaSituacao && (
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Situação</th>
                                        )}
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Ação</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                                    {sortedTerms.map((term, index) => {
                                        const rowKey = `${term.doc_tipo || aba}-${term.log_id ?? index}`;
                                        const registrando = registrandoKey === rowKey;
                                        const enviandoLembrete = enviandoLembreteKey === rowKey;
                                        const naoEnviando = naoEnviandoKey === rowKey;
                                        const assinado = !!term.assinado_em;
                                        const enviado = !!term.enviado_cliente_em;
                                        const naoEnviado = !!term.nao_enviado_cliente_em;
                                        const cancelado = !!term.cancelado_em;
                                        const cancelando = cancelandoKey === rowKey;
                                        // Documento apagado do Autentique: o link devolve 404, não vale oferecer.
                                        const documentoRemovido = term.autentique_resultado === 'removido' || term.autentique_resultado === 'sem_documento';
                                        const st = temColunaSituacao ? statusDoc(term) : null;
                                        return (
                                        <tr key={rowKey} className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors group align-top ${cancelado ? 'opacity-60' : ''}`}>
                                            {/* Documento: número + PF/PJ */}
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <FileText size={16} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                                    <span className={`text-sm font-mono text-gray-600 dark:text-slate-300 font-medium ${cancelado ? 'line-through' : ''}`}>{term.numero_termo || "N/A"}</span>
                                                </div>
                                                <span className={`ml-6 mt-1 inline-block text-[11px] font-bold px-1.5 py-0.5 rounded border ${term.tipo_cliente === "PJ"
                                                    ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/50"
                                                    : "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50"}`}>
                                                    {term.tipo_cliente || "-"}
                                                </span>
                                                {term.doc_tipo === 'aditivo' && term.tipo_aditivo && (
                                                    <span className="ml-1.5 mt-1 inline-block text-[11px] font-bold px-1.5 py-0.5 rounded border text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800/50">
                                                        {ADITIVO_LABEL[term.tipo_aditivo]}
                                                    </span>
                                                )}
                                            </td>
                                            {/* Associado: nome + quem gerou e quando */}
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-1.5 max-w-[220px]">
                                                    <User size={14} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-slate-200 truncate" title={term.associado || undefined}>{term.associado || "-"}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1 ml-[22px] max-w-[220px] text-[11px] text-gray-400 dark:text-slate-500">
                                                    <UserCheck size={12} className="text-primary flex-shrink-0" />
                                                    <span className="truncate" title={term.enviado_por || undefined}>{term.enviado_por || "-"}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-0.5 ml-[22px] text-[11px] text-gray-400 dark:text-slate-500">
                                                    <Calendar size={12} className="flex-shrink-0" />
                                                    {formatSafeDate(term.data_criacao)}
                                                </div>
                                            </td>
                                            {temColunaSituacao && st && (
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span title={st.title} className={`text-xs font-bold px-2 py-1 rounded-md border cursor-help ${st.cls}`}>{st.label}</span>
                                                    {cancelado ? (
                                                        <div className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 max-w-[220px] whitespace-normal">
                                                            {formatDia(term.cancelado_em)}{term.cancelado_por_nome ? ` · ${term.cancelado_por_nome}` : ''}
                                                            {term.cancelado_motivo && <div className="italic">"{term.cancelado_motivo}"</div>}
                                                        </div>
                                                    ) : enviado && (
                                                        <div className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">
                                                            Enviado {formatDia(term.enviado_cliente_em)}{term.enviado_cliente_por_nome ? ` · ${term.enviado_cliente_por_nome}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="inline-flex flex-col items-end gap-1.5">
                                                    {/* Documento apagado do Autentique não tem mais link — o antigo devolve 404. */}
                                                    {cancelado && documentoRemovido ? (
                                                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500 text-xs font-bold">
                                                            <Ban size={12} /> Documento removido
                                                        </span>
                                                    ) : (
                                                    /* Baixar PDF: assinado -> PDF assinado; senão, documento em elaboração no Autentique */
                                                    <a
                                                        href={assinado ? linkPdfAssinado(term) : term.url_pdf}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all shadow-sm text-xs font-bold ${assinado
                                                            ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
                                                            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                                                    >
                                                        {assinado ? <Download size={12} /> : <ExternalLink size={12} />}
                                                        {assinado ? 'Baixar PDF assinado' : (temFluxoEnvio ? 'Acessar documento' : 'Acessar termo')}
                                                    </a>
                                                    )}

                                                    {temFluxoEnvio && !cancelado && (<>
                                                    {/* Lembrete de assinatura ao procurador: só enquanto o status é "Aguardando assinatura" */}
                                                    {st?.key === 'aguardando' && onEnviarLembrete && (
                                                        <button
                                                            onClick={() => enviarLembrete(term, rowKey)}
                                                            disabled={enviandoLembrete}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 text-xs font-bold hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-60"
                                                        >
                                                            {enviandoLembrete ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                                                            {enviandoLembrete ? 'Enviando...' : 'Enviar lembrete'}
                                                        </button>
                                                    )}
                                                    {term.lembrete_enviado_em && (
                                                        <div className="text-[11px] text-gray-400 dark:text-slate-500">
                                                            Lembrete enviado {formatDia(term.lembrete_enviado_em)}{term.lembrete_enviado_por_nome ? ` · ${term.lembrete_enviado_por_nome}` : ''}
                                                        </div>
                                                    )}

                                                    {/* Registrar envio manual: só quando assinado, ainda não enviado e ninguém decidiu não enviar */}
                                                    {assinado && !enviado && !naoEnviado && onRegistrarEnvio && (
                                                        registrando ? (
                                                            <div className="flex items-center gap-1.5">
                                                                <input
                                                                    type="date"
                                                                    value={dataEnvio}
                                                                    max={hojeISO()}
                                                                    onChange={e => setDataEnvio(e.target.value)}
                                                                    className="text-xs px-2 py-1 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200"
                                                                />
                                                                <button
                                                                    onClick={() => confirmarEnvio(term)}
                                                                    disabled={salvando || !dataEnvio}
                                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-green-600 text-white text-xs font-bold hover:bg-green-700 disabled:opacity-60"
                                                                >
                                                                    {salvando ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                                                    Confirmar
                                                                </button>
                                                                <button
                                                                    onClick={() => setRegistrandoKey(null)}
                                                                    disabled={salvando}
                                                                    className="px-2 py-1 rounded-md text-xs text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                                                                >
                                                                    Cancelar
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => { setDataEnvio(hojeISO()); setRegistrandoKey(rowKey); }}
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-700 dark:text-green-400 text-xs font-bold hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                                                            >
                                                                <Send size={12} /> Registrar envio ao cliente
                                                            </button>
                                                        )
                                                    )}

                                                    {/* Decisão deliberada de não encaminhar ao associado, com motivo — alternativa ao envio, disponível em qualquer estágio */}
                                                    {!enviado && !naoEnviado && onRegistrarNaoEnvio && (
                                                        naoEnviando ? (
                                                            <div className="flex flex-col items-end gap-1.5 w-56">
                                                                <textarea
                                                                    value={motivoNaoEnvio}
                                                                    onChange={e => setMotivoNaoEnvio(e.target.value)}
                                                                    placeholder="Por que não será enviado?"
                                                                    rows={2}
                                                                    className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 resize-none"
                                                                />
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        onClick={() => confirmarNaoEnvio(term)}
                                                                        disabled={salvandoNaoEnvio || !motivoNaoEnvio.trim()}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-600 text-white text-xs font-bold hover:bg-gray-700 disabled:opacity-60"
                                                                    >
                                                                        {salvandoNaoEnvio ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                                                        Confirmar
                                                                    </button>
                                                                    <button
                                                                        onClick={() => { setNaoEnviandoKey(null); setMotivoNaoEnvio(''); }}
                                                                        disabled={salvandoNaoEnvio}
                                                                        className="px-2 py-1 rounded-md text-xs text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => { setMotivoNaoEnvio(''); setNaoEnviandoKey(rowKey); }}
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                                            >
                                                                <BellOff size={12} /> Não enviar
                                                            </button>
                                                        )
                                                    )}
                                                    {naoEnviado && (
                                                        <div className="text-[11px] text-gray-400 dark:text-slate-500 max-w-[220px] text-right">
                                                            Marcado como não enviado {formatDia(term.nao_enviado_cliente_em)}{term.nao_enviado_cliente_por_nome ? ` · ${term.nao_enviado_cliente_por_nome}` : ''}
                                                            {term.nao_enviado_cliente_motivo && <div className="italic">"{term.nao_enviado_cliente_motivo}"</div>}
                                                        </div>
                                                    )}
                                                    </>)}

                                                    {/* Desfazer o documento. Assinado -> anula (o PDF sobrevive);
                                                        não assinado -> apaga do Autentique e o link morre. Quem decide
                                                        é o estado real no Autentique, não esta tela. */}
                                                    {podeCancelar && !cancelado && term.log_id && (
                                                        cancelando ? (
                                                            <div className="flex flex-col items-end gap-1.5 w-64">
                                                                <textarea
                                                                    value={motivoCancelamento}
                                                                    onChange={e => setMotivoCancelamento(e.target.value)}
                                                                    placeholder={cancelandoIntencao === 'corrigir'
                                                                        ? 'O que estava errado no documento?'
                                                                        : 'Por que este documento está sendo cancelado?'}
                                                                    rows={2}
                                                                    autoFocus
                                                                    className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 resize-none"
                                                                />
                                                                <p className="text-[11px] text-gray-400 dark:text-slate-500 text-right leading-snug">
                                                                    {assinado
                                                                        ? 'Já assinado: o documento será marcado como recusado no Autentique e o PDF continua acessível.'
                                                                        : 'Ainda não assinado: o documento será apagado do Autentique e o link de assinatura deixa de funcionar.'}
                                                                    {' '}Não dá para desfazer.
                                                                </p>
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        onClick={() => confirmarCancelamento(term)}
                                                                        disabled={salvandoCancelamento || motivoCancelamento.trim().length < MOTIVO_MINIMO}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-60"
                                                                    >
                                                                        {salvandoCancelamento ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                                                        {cancelandoIntencao === 'corrigir' ? 'Cancelar e refazer' : 'Confirmar'}
                                                                    </button>
                                                                    <button
                                                                        onClick={fecharFormCancelamento}
                                                                        disabled={salvandoCancelamento}
                                                                        className="px-2 py-1 rounded-md text-xs text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                                                                    >
                                                                        Voltar
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5">
                                                                {onCorrigirDocumento && (
                                                                    <button
                                                                        onClick={() => { setMotivoCancelamento(''); setCancelandoIntencao('corrigir'); setCancelandoKey(rowKey); }}
                                                                        title="Cancela este documento e reabre o formulário de geração já preenchido, para você corrigir e emitir de novo."
                                                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                                                    >
                                                                        <PencilLine size={12} /> Corrigir
                                                                    </button>
                                                                )}
                                                                {onCancelarDocumento && (
                                                                    <button
                                                                        onClick={() => { setMotivoCancelamento(''); setCancelandoIntencao('cancelar'); setCancelandoKey(rowKey); }}
                                                                        title={assinado
                                                                            ? 'Anula o documento: ele fica marcado como recusado no Autentique e o PDF continua acessível.'
                                                                            : 'Apaga o documento do Autentique. O link de assinatura deixa de funcionar.'}
                                                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                                                    >
                                                                        <Trash2 size={12} /> {assinado ? 'Anular' : 'Excluir'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    )}

                                                    {cancelado && term.autentique_resultado === 'falhou' && (
                                                        <div className="text-[11px] text-orange-600 dark:text-orange-400 max-w-[240px] text-right whitespace-normal">
                                                            O Autentique não confirmou a operação — confira pelo painel.
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TermCancelHistoryModal;
